"use client"

import { useState, useEffect } from "react"
import { DashboardHeader } from "@/components/dashboard-header"
import { DashboardShell } from "@/components/dashboard-shell"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { Button } from "@/components/ui/button"
import { PaymentHistory } from "@/components/payment-history"
import { FinancialDashboard } from "@/components/financial-dashboard"
import { FinancialAnalytics } from "@/components/financial-analytics"
import { OutstandingPayments } from "@/components/outstanding-payments"
import { PaymentFormModal } from "@/components/payment-form-modal"
import { Payment } from "@/types"
import { useAuth } from "@/contexts/auth-context"
import { PlusCircle } from "lucide-react"
import { Doctor } from "@/types";
import { supabase } from "@/utils/supabase/client"

export default function PaymentsPage() {
  const [activeTab, setActiveTab] = useState("payment-history")
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [paymentToEdit, setPaymentToEdit] = useState<Payment | null>(null)
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  
  // Need a way to refresh the history list after a save
  const [refreshKey, setRefreshKey] = useState(0)

  const { currentOrganization } = useAuth()

  useEffect(() => {
    const fetchDoctors = async () => {
      if (!currentOrganization) return;
      const { data, error } = await supabase
        .from('doctors')
        .select('*')
        .eq('organization_id', currentOrganization.id);
      if (data) setDoctors(data);
    };
    if (currentOrganization) {
      fetchDoctors();
    }
  }, [currentOrganization]);

  const handleEdit = (payment: Payment) => {
    setPaymentToEdit(payment);
    setIsModalOpen(true);
  }
  
  const handleSave = () => {
    setRefreshKey(oldKey => oldKey + 1)
  }

  const handleOpenModal = (payment: Payment | null = null) => {
    setPaymentToEdit(payment);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setPaymentToEdit(null);
  };

  const handlePaymentSaved = () => {
    handleCloseModal();
    setRefreshKey(prev => prev + 1);
  }

  return (
    <div className="md:ml-64">
      <PaymentFormModal 
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        paymentToEdit={paymentToEdit}
        doctors={doctors}
      />
      <DashboardShell>
        <DashboardHeader heading="Financial Management" text="Monitor and manage your clinic's finances." >
            <Button onClick={() => handleOpenModal()}>
                <PlusCircle className="mr-2 h-4 w-4" /> Add New Payment
            </Button>
        </DashboardHeader>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="payment-history">Payment History</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            <TabsTrigger value="outstanding">Outstanding</TabsTrigger>
          </TabsList>
          <TabsContent value="dashboard">
            <FinancialDashboard />
          </TabsContent>
          <TabsContent value="payment-history">
             <PaymentHistory onEdit={handleEdit} key={refreshKey} />
          </TabsContent>
          <TabsContent value="analytics">
             {currentOrganization && <FinancialAnalytics organizationId={currentOrganization.id} />}
          </TabsContent>
           <TabsContent value="outstanding">
             <OutstandingPayments />
          </TabsContent>
        </Tabs>
      </DashboardShell>
    </div>
  )
} 